import React, { useRef, useState, useEffect } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import type { XYCoord } from 'react-dnd'
import { GripVertical, Upload } from 'lucide-react'
import { Input } from '../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'

interface SortablePromotionItemProps {
  index: number
  item: any
  moveItem: (dragIndex: number, hoverIndex: number) => void
  onFileChange: (file: File) => void
  register: any
  errors: any
  title: string
  activePromotions?: any[]
  setValue?: any
}

const ItemTypes = {
  PROMOTION_ITEM: 'promotion_item',
}

export function SortablePromotionItem({ index, item, moveItem, onFileChange, register, errors, title, activePromotions = [], setValue }: SortablePromotionItemProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [linkType, setLinkType] = useState<'url' | 'promotion'>(item.promotion_id ? 'promotion' : 'url')
  
  const [{ handlerId }, drop] = useDrop<
    { index: number },
    void,
    { handlerId: string | symbol | null }
  >({
    accept: ItemTypes.PROMOTION_ITEM,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(draggedItem, monitor) {
      if (!ref.current) return
      const dragIndex = draggedItem.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top
      
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return
      
      moveItem(dragIndex, hoverIndex)
      draggedItem.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.PROMOTION_ITEM,
    item: () => {
      return { index }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onFileChange(e.target.files[0])
    }
  }

  const handleLinkTypeChange = (value: 'url' | 'promotion') => {
    setLinkType(value)
    if (setValue) {
      if (value === 'url') {
        setValue(`items.${index}.promotion_id`, null)
      } else {
        setValue(`items.${index}.call_to_action_url`, '')
      }
    }
  }

  return (
    <div ref={ref} data-handler-id={handlerId} className={`flex gap-4 p-4 mb-2 border rounded-md bg-white ${isDragging ? 'opacity-50' : 'opacity-100'}`}>
      <div className="flex items-center cursor-move text-slate-400">
        <GripVertical />
      </div>
      
      <div className="flex-1 space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="font-semibold text-sm">{title}</h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-medium">Imagen <span className="text-red-500">*</span></label>
            {item.preview_url ? (
              <div className="relative group border rounded-md p-1 h-32 flex items-center justify-center bg-slate-50">
                <img src={item.preview_url} alt="preview" className="max-h-full max-w-full object-contain" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-md">
                   <label className="cursor-pointer bg-white p-2 rounded-full text-slate-800">
                     <Upload size={16} />
                     <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                   </label>
                </div>
              </div>
            ) : (
              <label className="border-2 border-dashed rounded-md h-32 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50">
                <Upload className="text-slate-400 mb-2" size={24} />
                <span className="text-xs text-slate-500">Subir imagen</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
              </label>
            )}
            {errors?.items?.[index]?.image_url && (
              <p className="text-xs text-red-500">{errors.items[index].image_url.message}</p>
            )}
          </div>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-medium">Nombre de la Card <span className="text-red-500">*</span></label>
              <Input {...register(`items.${index}.name`)} placeholder="Ej. Oferta Lunes" />
              {errors?.items?.[index]?.name && (
                <p className="text-xs text-red-500">{errors.items[index].name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium">Tipo de Enlace</label>
              <Select value={linkType} onValueChange={handleLinkTypeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona el tipo de enlace" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="url">URL Manual</SelectItem>
                  <SelectItem value="promotion">Promoción Interna</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {linkType === 'url' ? (
              <div className="space-y-2">
                <label className="text-xs font-medium">URL del CTA (Opcional)</label>
                <Input {...register(`items.${index}.call_to_action_url`)} placeholder="https://..." />
                {errors?.items?.[index]?.call_to_action_url && (
                  <p className="text-xs text-red-500">{errors.items[index].call_to_action_url.message}</p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-xs font-medium">Promoción (Opcional)</label>
                <Select 
                  value={item.promotion_id ? String(item.promotion_id) : ''} 
                  onValueChange={(val) => {
                    if (setValue) {
                       setValue(`items.${index}.promotion_id`, val === 'none' ? null : Number(val))
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una promoción activa" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Sin enlazar --</SelectItem>
                    {activePromotions.map((promo: any) => (
                      <SelectItem key={promo.id} value={String(promo.id)}>
                        {promo.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors?.items?.[index]?.promotion_id && (
                  <p className="text-xs text-red-500">{errors.items[index].promotion_id.message}</p>
                )}
              </div>
            )}

            <div className="flex items-center gap-2">
              <input type="checkbox" id={`active-${index}`} {...register(`items.${index}.is_active`)} className="rounded border-slate-300 text-primary focus:ring-primary h-4 w-4" />
              <label htmlFor={`active-${index}`} className="text-xs font-medium cursor-pointer">Activo</label>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
